import React, { useEffect, useRef } from 'react';
import { Alert } from 'antd';

declare global {
    interface Window {
        grecaptcha?: any;
        __oscarOnRecaptchaLoaded?: () => void;
    }
}

interface RecaptchaWidgetProps {
    siteKey: string;
    onTokenChange: (token: string | null) => void;
}

const SCRIPT_ID = 'google-recaptcha-script';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

const RecaptchaWidget: React.FC<RecaptchaWidgetProps> = ({ siteKey, onTokenChange }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!siteKey) return;

        const renderWidget = () => {
            if (!window.grecaptcha || !containerRef.current || widgetIdRef.current !== null) {
                return;
            }

            widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
                sitekey: siteKey,
                callback: (token: string) => onTokenChange(token),
                'expired-callback': () => onTokenChange(null),
                'error-callback': () => onTokenChange(null),
            });
        };

        const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (existingScript) {
            if (window.grecaptcha) {
                renderWidget();
            } else {
                const previousOnLoad = window.__oscarOnRecaptchaLoaded;
                window.__oscarOnRecaptchaLoaded = () => {
                    previousOnLoad?.();
                    renderWidget();
                };
            }
            return;
        }

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        const previousOnLoad = window.__oscarOnRecaptchaLoaded;
        window.__oscarOnRecaptchaLoaded = () => {
            previousOnLoad?.();
            renderWidget();
        };
        script.onload = () => {
            window.__oscarOnRecaptchaLoaded?.();
        };
        document.body.appendChild(script);
    }, [siteKey, onTokenChange]);

    useEffect(() => {
        return () => {
            onTokenChange(null);
        };
    }, [onTokenChange]);

    if (!siteKey) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Captcha is not configured."
                description="Set REACT_APP_RECAPTCHA_SITE_KEY to enable registration captcha."
            />
        );
    }

    return <div ref={containerRef} />;
};

export default RecaptchaWidget;

